//#region IMPORTS
import { SHOP_TAB, ShopTab, TAB_LABELS } from "../../components/orders/tabs";
import {
  INSTORE_TYPE_BUCKETS,
  INSTORE_TYPE_BUCKET_COLOR,
} from "../../components/orders/instore/instorePresets";
import { useHistory, useLocation } from "react-router-dom";
import { useAppSelector } from "../../store/configureStore";
import InstoreOrdersView from "../../components/orders/instore/InstoreOrdersView";
import usePickerManager from "../../hooks/usePickerManager";
import PromoOrdersView from "../../components/orders/promo/PromoOrdersView";
import useAdminManager from "../../hooks/useAdminManager";
import useHQmanager from "../../hooks/useHQmanager";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  title: string;
}
//#endregion

export default function OrdersHistoryPage(_: Props) {
  return <OrdersTabsView />;
}

//#region TABBED VIEW
function OrdersTabsView() {
  const location = useLocation();
  const history = useHistory();

  const queryTab = new URLSearchParams(location.search).get("tab") as
    | ShopTab
    | null;
  const validTabs = Object.values(SHOP_TAB) as ShopTab[];
  const activeTab: ShopTab =
    queryTab && validTabs.includes(queryTab) ? queryTab : SHOP_TAB.promo;

  const setTab = (tab: ShopTab) => {
    history.replace(`/orders?tab=${tab}`);
  };

  return (
    <>
      <div className="bg-white px-8 pt-2">
        <nav className="flex gap-1 border-b border-gray-200" aria-label="Tabs">
          {validTabs.map((id) => (
            <OrdersTabButton
              key={id}
              tabId={id}
              active={activeTab === id}
              onClick={() => setTab(id)}
            />
          ))}
        </nav>
      </div>

      <OrdersTabContent activeTab={activeTab} />
    </>
  );
}

function OrdersTabButton({
  tabId,
  active,
  onClick,
}: {
  tabId: ShopTab;
  active: boolean;
  onClick: () => void;
}) {
  const { user } = useAppSelector((state) => state.account);
  const isPicker = usePickerManager(user);
  const isAdmin = useAdminManager(user);
  const isHq = useHQmanager(user);

  const { orders_pending, orders_ordered, orders_inProgress } = useAppSelector(
    (state) => state.orders,
  );
  const {
    instoreOrders_pending,
    instoreOrders_ordered,
    instoreOrders_inProgress,
    instoreFlowCounts,
  } = useAppSelector((state) => state.instoreOrders);

  // Sum of the flow pills the role sees:
  // HQ → Approve (Pending); admin → Approve + Manage + Send;
  // picker → Manage + Send (pickers don't approve promo).
  const promoCount = isHq
    ? orders_pending
    : isPicker
      ? orders_ordered + orders_inProgress
      : isAdmin
        ? orders_pending + orders_ordered + orders_inProgress
        : 0;
  // Sum of the flow pills the role sees:
  // HQ → Approve (Pending); picker / admin → Approve + Manage + Send.
  const instoreCount = isHq
    ? instoreOrders_pending
    : isPicker || isAdmin
      ? instoreOrders_pending +
        instoreOrders_ordered +
        instoreOrders_inProgress
      : 0;

  const count = tabId === SHOP_TAB.promo ? promoCount : instoreCount;
  const showBadge = count > 0;
  const badgeColor =
    tabId === SHOP_TAB.promo ? "bg-red-500" : "bg-purple-600";

  // Picker/admin see the instore tab count split per type bucket (Week / Opening
  // / Relocation), summed across the workflow statuses — mirroring the flow pills.
  const splitInstore = tabId === SHOP_TAB.instore && (isPicker || isAdmin);
  const instoreBuckets = INSTORE_TYPE_BUCKETS.map((type) => ({
    type,
    count:
      instoreFlowCounts.pending[type] +
      instoreFlowCounts.ordered[type] +
      instoreFlowCounts.inProgress[type],
  })).filter((b) => b.count > 0);

  return (
    <button
      onClick={onClick}
      className={classNames(
        "relative px-4 py-3 text-sm font-medium border-b-2 -mb-px transition inline-flex items-center gap-2",
        active
          ? "border-black text-black"
          : "border-transparent text-gray-500 hover:text-gray-800",
      )}
    >
      {TAB_LABELS[tabId]}
      {splitInstore
        ? instoreBuckets.length > 0 && (
            <span className="flex -space-x-0.5">
              {instoreBuckets.map((b) => (
                <span
                  key={b.type}
                  className={classNames(
                    "inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-xs font-semibold ring-2 ring-white",
                    INSTORE_TYPE_BUCKET_COLOR[b.type],
                  )}
                >
                  {b.count}
                </span>
              ))}
            </span>
          )
        : showBadge && (
            <span
              className={classNames(
                "inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-xs font-semibold text-white",
                badgeColor,
              )}
            >
              {count}
            </span>
          )}
    </button>
  );
}

function OrdersTabContent({ activeTab }: { activeTab: ShopTab }) {
  switch (activeTab) {
    case SHOP_TAB.instore:
      return <InstoreOrdersView />;
    default:
      return <PromoOrdersView />;
  }
}
//#endregion
